// Google Identity Services (GIS) sign-in.
// Replaces the old Azure AD B2C / MSAL flow. The backend (func-qanda-vsjbf7)
// verifies the Google ID token, so we send that token straight through as a
// Bearer credential — no extra token exchange needed.
//
// Client ID is injected at build time via VUE_APP_GOOGLE_CLIENT_ID and must
// match GoogleAuth__ClientId on the Function App. The page origin (e.g. the
// staging URL) must also be registered as an "Authorized JavaScript origin"
// on that OAuth client in Google Cloud Console, or sign-in will be rejected.

import store from '@/store'

const GSI_SRC = 'https://accounts.google.com/gsi/client'
const CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID

let readyPromise = null
let currentToken = localStorage.getItem('googleToken') || null

// Load the GIS script and resolve when window.google is ready.
function loadGsi() {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      resolve(window.google)
      return
    }
    const script = document.createElement('script')
    script.src = GSI_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve(window.google)
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'))
    document.head.appendChild(script)
  })
}

// Idempotent: load + initialise GIS once. A successful sign-in updates the
// store (which the rest of the app reacts to). Safe to call from any component.
function ensureGoogleAuth() {
  if (readyPromise) return readyPromise
  readyPromise = (async () => {
    if (!CLIENT_ID) {
      console.error('VUE_APP_GOOGLE_CLIENT_ID is not set — Google Sign-In is disabled.')
      return null
    }
    const google = await loadGsi()
    google.accounts.id.initialize({
      client_id: CLIENT_ID,
      auto_select: false,
      cancel_on_tap_outside: true,
      callback: (response) => {
        currentToken = response.credential
        localStorage.setItem('googleToken', currentToken)
        store.dispatch('setGoogleAuth', currentToken)
      },
    })
    return google
  })()
  return readyPromise
}

// Render the official Google button into `el`. Must run after ensureGoogleAuth().
// `opts` overrides the defaults (e.g. { width: 240 } to match a custom button).
function renderGoogleButton(el, opts = {}) {
  if (!window.google || !window.google.accounts || !el) return
  el.innerHTML = ''
  window.google.accounts.id.renderButton(el, {
    type: 'standard',
    theme: 'outline',
    size: 'large',
    text: 'signin_with',
    shape: 'pill',
    logo_alignment: 'left',
    ...opts,
  })
}

function getGoogleToken() {
  return currentToken || localStorage.getItem('googleToken') || ''
}

function googleSignOut() {
  currentToken = null
  localStorage.removeItem('googleToken')
  if (window.google && window.google.accounts && window.google.accounts.id) {
    window.google.accounts.id.disableAutoSelect()
  }
}

export { ensureGoogleAuth, renderGoogleButton, getGoogleToken, googleSignOut }
